/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

import { getConfig, setConfig } from '../lib/config.js';
import { setLanguage } from '../lib/i18n.js';

/*
 * Reads the configuration the backend injected, and loads the tenant's
 * fonts.
 *
 * The `#maps-config` island is the whole contract between the two sides,
 * so a page that arrives without one is not a Map Viewer page at all — the
 * config stays empty and every feature that reads it stays switched off,
 * rather than the Map Viewer failing on its first lookup.
 */

/**
 * Parse the configuration island into the config module.
 *
 * @param {Document|Element} [root] - Where to look for the island.
 * @returns {object} The parsed configuration.
 */
export function readConfig(root = document) {
    const element = root.querySelector('#maps-config');
    let parsed = {};
    try {
        parsed = JSON.parse(element?.textContent ?? '{}');
    } catch (error) {
        console.error(`Error parsing configuration: ${error}`);
    }

    // The token arrives as the string "null" when the template had nothing
    // to put there, and every caller then sends it as a real token.
    if (parsed?.access?.token === 'null') {
        parsed.access.token = null;
    }

    setConfig({ ...getConfig(), ...parsed });
    // The interface follows the map's language, not the browser's: a
    // reader who opened the French version wants French controls.
    setLanguage(getConfig()?.i18n?.lang || document.documentElement.lang);
    return getConfig();
}

/**
 * Load the tenant's webfonts.
 *
 * Deferred to idle time: the map is readable in the fallback face, and
 * blocking the first render on a stylesheet request makes it not be.
 *
 * @returns {Promise<number>} How many stylesheets were added.
 */
export async function loadClientFonts() {
    const url = getConfig()?.endpoints?.clientFonts;
    if (!url) return 0;

    try {
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) {
            throw new Error(`${response.status} ${response.statusText}`);
        }
        const { fonts = [] } = await response.json();
        const hrefs = new Set(
            fonts.filter((font) => font?.source).map((font) => font.source)
        );
        for (const href of hrefs) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = href;
            document.head.appendChild(link);
        }
        return hrefs.size;
    } catch (error) {
        console.error(`Could not load client fonts from "${url}":`, error);
        return 0;
    }
}

/** Read the configuration, then schedule the fonts. */
export function init() {
    readConfig();
    const schedule =
        'requestIdleCallback' in window
            ? window.requestIdleCallback
            : (fn) => setTimeout(fn, 0);
    schedule(() => loadClientFonts());
}
